import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Play } from 'lucide-react';
import { SpeechBubble } from '../components/ui/SpeechBubble';
import { FloatingDecor } from '../components/ui/FloatingDecor';
const sectionEntrance = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    type: 'spring',
    stiffness: 200,
    damping: 20
  }
};
const popWord = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: 0
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: -2
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15,
    delay: 0.3
  }
};
// Image de secours (SVG inline) si une photo externe ne charge pas — évite l'icône d'image cassée
const FALLBACK_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='600'%3E%3Crect width='100%25' height='100%25' fill='%23FFE4ED'/%3E%3C/svg%3E";
const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = FALLBACK_IMG;
};
// Embed TikTok officiel (blockquote + embed.js), plus fiable que l'iframe brute
// dans un aperçu qui est lui-même déjà dans un iframe (Magic Patterns).
// On recharge le script à chaque montage pour forcer TikTok à hydrater les
// nouveaux blockquotes (nécessaire en navigation SPA, pas seulement au 1er chargement).
function TikTokEmbed({
  videoId,
  url
}: {
  videoId: string;
  url: string;
}) {
  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.tiktok.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);
  return <div className="tiktok-embed-wrapper bg-jany-dark" data-id="element-276">
      <blockquote className="tiktok-embed" cite={url} data-video-id={videoId} style={{
      maxWidth: '100%',
      minWidth: '100%',
      margin: 0
    }} data-id="element-277">
        <section data-id="element-278">
          <a target="_blank" href={url} rel="noopener noreferrer" data-id="element-279">
            Voir la vidéo sur TikTok
          </a>
        </section>
      </blockquote>
      <a href={url} target="_blank" rel="noopener noreferrer" className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 bg-white/90 text-jany-dark text-xs font-bold px-3 py-1.5 rounded-full shadow hover:bg-white" data-id="element-280">
        <Play size={12} fill="currentColor" data-id="element-281" /> TikTok
      </a>
    </div>;
}
// Vraies collaborations de l'agence (d'après le feed Instagram @janyagency) + vidéos TikTok envoyées par Samir
// Photos vérifiées individuellement sur Unsplash (recherche + lecture de page) pour éviter les liens cassés
const gridItems = [{
  type: 'brand',
  img: 'https://images.unsplash.com/photo-1725522845933-a7d6572e7c3e?auto=format&fit=crop&q=80&w=600',
  alt: 'Femme allongée sur une serviette de plage',
  brand: 'paalm.',
  tagline: 'Brume de parfum corps & cheveux — édition été "peachee lychee"'
}, {
  type: 'video',
  videoId: '7653076840805960992',
  url: 'https://www.tiktok.com/@rayan.blf/video/7653076840805960992'
}, {
  type: 'words',
  color: 'bg-jany-orange',
  words: ['TALENTS', 'BRANDS', 'CONTENT', 'INFLUENCE']
}, {
  type: 'video',
  videoId: '7604231761664544022',
  url: 'https://www.tiktok.com/@mvjdolinee/video/7604231761664544022'
}, {
  type: 'brand',
  img: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&q=80&w=600',
  alt: 'Femme marchant sur la côte avec un sac',
  brand: 'Owari',
  tagline: 'Compléments bien-être — univers lifestyle & voyage'
}, {
  type: 'video',
  videoId: '7646470399588306208',
  url: 'https://www.tiktok.com/@marine__mkdm/video/7646470399588306208'
}, {
  type: 'logo'
}, {
  type: 'video',
  videoId: '7488429453408800022',
  url: 'https://www.tiktok.com/@marine__mkdm/video/7488429453408800022'
}, {
  type: 'brand',
  img: 'https://images.unsplash.com/photo-1544717304-a2db4a7b16ee?auto=format&fit=crop&q=80&w=600',
  alt: 'Femme en serviette blanche, ambiance spa',
  brand: 'Musc Intime',
  tagline: '"Confidence starts within." — collection soins intimes'
}, {
  type: 'text',
  color: 'bg-jany-pink',
  text: 'We create impactful stories.',
  textColor: 'text-white'
}, {
  type: 'text',
  color: 'bg-jany-blue',
  text: 'Behind every brand is the right story.',
  textColor: 'text-jany-dark'
}];
export function Portfolio() {
  return <div className="pt-32 pb-0 overflow-hidden" data-id="element-282">
      <div className="container mx-auto px-6 md:px-12 mb-20 relative" data-id="element-283">
        <FloatingDecor color="pink" count={4} data-id="element-284" />
        <div className="text-center max-w-3xl mx-auto relative z-10" data-id="element-285">
          <SpeechBubble text="OUR WORK" color="blue" className="mb-8" data-id="element-286" />
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-6xl md:text-8xl mb-8 text-jany-dark" data-id="element-287">
            Nos{' '}
            <motion.span variants={popWord} initial="initial" animate="animate" className="inline-block text-jany-pink italic" data-id="element-288">
              Réalisations
            </motion.span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-2xl text-jany-dark/80 font-medium" data-id="element-289">
            paalm., Owari, Musc Intime... un aperçu de nos collaborations
            récentes. Des contenus esthétiques, authentiques et performants.
          </motion.p>
        </div>
      </div>

      {/* Instagram-style Masonry Grid */}
      <motion.section {...sectionEntrance} className="bg-jany-yellow py-24 rounded-t-[4rem] relative" data-id="element-290">
        <FloatingDecor color="dark" count={10} data-id="element-291" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-292">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-293">
            {gridItems.map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: idx % 3 * 0.1
          }} whileHover={{
            scale: item.type === 'video' ? 1 : 1.03,
            rotate: item.type === 'video' ? 0 : idx % 2 === 0 ? 1 : -1
          }} className="aspect-square rounded-[3rem] overflow-hidden shadow-lg border-8 border-white relative" data-id="element-294">
                {item.type === 'text' && <div className={`w-full h-full ${item.color} flex items-center justify-center p-10 text-center`} data-id="element-295">
                    <h3 className={`font-display text-3xl md:text-5xl leading-tight ${item.textColor} italic`} data-id="element-296">
                      "{item.text}"
                    </h3>
                  </div>}

                {item.type === 'words' && <div className={`w-full h-full ${item.color} flex flex-col items-center justify-center gap-4 px-6`} data-id="element-297">
                    {item.words.map((w, i) => <span key={i} className="text-white text-base md:text-lg font-medium tracking-[0.2em] uppercase" data-id="element-298">
                        {w}
                      </span>)}
                    <Sparkles className="text-jany-yellow mt-2" size={20} data-id="element-299" />
                    <span className="text-white/80 text-xs tracking-[0.25em] uppercase mt-2" data-id="element-300">
                      Jany & Co
                    </span>
                  </div>}

                {item.type === 'brand' && <div className="relative w-full h-full" data-id="element-301">
                    <img src={item.img} alt={item.alt} onError={handleImgError} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" data-id="element-302" />
                    <div className="absolute inset-0 bg-gradient-to-t from-jany-dark/80 via-jany-dark/10 to-transparent" data-id="element-303" />
                    <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-304">
                      <p className="font-display text-2xl md:text-3xl text-white mb-1" data-id="element-305">
                        {item.brand}
                      </p>
                      <p className="text-white/90 text-xs md:text-sm font-medium leading-snug" data-id="element-306">
                        {item.tagline}
                      </p>
                    </div>
                  </div>}

                {item.type === 'logo' && <div className="w-full h-full bg-jany-yellow flex flex-col items-center justify-center gap-4" data-id="element-307">
                    <Sparkles className="text-jany-pink" size={36} data-id="element-308" />
                    <div className="text-center font-display leading-none" data-id="element-309">
                      <div className="text-jany-pink text-4xl md:text-5xl" data-id="element-310">
                        JANY
                      </div>
                      <div className="text-jany-pink text-3xl md:text-4xl -mt-1" data-id="element-311">
                        & CO
                      </div>
                    </div>
                    <p className="text-jany-pink text-[0.6rem] md:text-xs font-bold tracking-[0.25em] uppercase" data-id="element-312">
                      Talent & Influence Agency
                    </p>
                  </div>}

                {item.type === 'video' && <TikTokEmbed videoId={item.videoId} url={item.url} data-id="element-313" />}
              </motion.div>)}
          </div>

          <div className="mt-24 text-center" data-id="element-314">
            <motion.a whileHover={{
            scale: 1.05,
            rotate: 2
          }} whileTap={{
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} href="https://instagram.com/janyagency" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-white text-jany-pink font-bold text-xl px-10 py-5 rounded-full shadow-lg hover:bg-jany-pink hover:text-white transition-colors duration-300" data-id="element-315">
              Voir plus sur Instagram <span className="ml-3 text-2xl" data-id="element-316">→</span>
            </motion.a>
          </div>
        </div>
      </motion.section>
    </div>;
}